import { useState } from "react";
import confetti from "canvas-confetti";
import type { ValidationResult } from "@/services/QuizValidationService";
import {
  isResolved,
  seedInputs,
  seedResults,
  ValidationAttemptsExceededError,
  NO_ATTEMPTS_LEFT_FEEDBACK,
  type ValidationSubmission,
} from "@/lib/validationSubmission";

interface Options<TEntry> {
  entries: TEntry[];
  initialSubmissions?: Record<number, ValidationSubmission>;
  onValidate: (input: string, index: number) => Promise<ValidationResult>;
  onSubmitted?: () => void;
  getDefaultInput: (entry: TEntry) => string;
  /** When false, a successful submit calls onAdvance and shows no verdict. */
  revealFeedback?: boolean;
  onAdvance?: () => void;
}

export function useQuizCodeState<TEntry>({
  entries,
  initialSubmissions,
  onValidate,
  onSubmitted,
  getDefaultInput,
  revealFeedback = true,
  onAdvance,
}: Options<TEntry>) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userInputs, setUserInputs] = useState<Record<number, string>>(() =>
    seedInputs(initialSubmissions)
  );
  const [results, setResults] = useState<Record<number, ValidationResult>>(() =>
    seedResults(initialSubmissions)
  );
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentEntry = entries[currentIndex] ?? null;
  const currentInput =
    currentEntry !== null
      ? (userInputs[currentIndex] ?? getDefaultInput(currentEntry))
      : "";
  const currentResult = results[currentIndex];
  const isPassed = currentResult?.correct === true;
  // Capped types stop accepting code once the verdict is right or the budget
  // is spent; uncapped ones only settle on a correct answer.
  const isSettled =
    currentResult != null &&
    isResolved({ ...currentResult, correct: currentResult.correct ?? null });
  const isMultiple = entries.length > 1;
  const isLast = currentIndex === entries.length - 1;
  const progressLabel =
    isMultiple && `Aufgabe ${currentIndex + 1} von ${entries.length}`;

  const handleInputChange = (value: string) => {
    setUserInputs((prev) => ({ ...prev, [currentIndex]: value }));
  };

  const handleSubmit = async () => {
    setIsValidating(true);
    setError(null);
    try {
      const result = await onValidate(currentInput, currentIndex);
      if (!revealFeedback) {
        onAdvance?.();
        return;
      }
      setResults((prev) => ({ ...prev, [currentIndex]: result }));
      onSubmitted?.();
      if (result.correct) {
        confetti({ particleCount: 150, spread: 80, origin: { y: 0.6 } });
      }
    } catch (submissionError) {
      // The server is the authority on the budget: when it refuses the
      // submission the last verdict stands, only the counter catches up.
      if (submissionError instanceof ValidationAttemptsExceededError) {
        setResults((prev) => ({
          ...prev,
          [currentIndex]: {
            correct: prev[currentIndex]?.correct ?? false,
            grade: prev[currentIndex]?.grade,
            feedback: prev[currentIndex]?.feedback ?? NO_ATTEMPTS_LEFT_FEEDBACK,
            attemptCount: submissionError.attemptCount,
            maxAttempts: submissionError.maxAttempts,
          },
        }));
        return;
      }
      setError("Die Prüfung ist fehlgeschlagen. Bitte versuche es erneut.");
    } finally {
      setIsValidating(false);
    }
  };

  const handleNext = () => {
    if (currentIndex < entries.length - 1) {
      setCurrentIndex((i) => i + 1);
      setError(null);
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex((i) => i - 1);
      setError(null);
    }
  };

  return {
    currentIndex,
    currentEntry,
    currentInput,
    currentResult,
    isPassed,
    isSettled,
    isMultiple,
    isLast,
    progressLabel,
    isValidating,
    error,
    handleInputChange,
    handleSubmit,
    handleNext,
    handleBack,
  };
}
